import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type {
  PublicOpenScheduleDto,
  PublicRangeDto,
  PublicSchoolDto,
} from '@motoschool/shared';
import {
  BOOKING_START_STEP_MINUTES,
  MIN_LESSON_MINUTES,
  bookingStartOptions,
  totalMinutes,
} from '@motoschool/shared';
import { formatMoney } from '@motoschool/shared';
import { api } from '../lib/api';
import { useAuth } from '../auth/AuthContext';
import { Avatar, cn } from '../components/ui';
import { SchoolName } from '../components/SchoolName';
import { MotoIcon, type MotoIconName } from '../components/MotoIcons';
import { Logo } from '../components/Logo';
import { ContactSheet, phoneDigits } from '../components/ContactSheet';
import { COURSES } from './CoursePage';
import { ExerciseFigure, EXERCISE_TITLES } from '../components/ExerciseFigure';
import { formatTime, humanDayLabel } from '../lib/format';
import { usePageMeta, useSchoolSchema } from '../lib/seo';
import { LANDING_META } from '../lib/routeMeta';

/* --------------------------------------------------------------------------
 * Тексты страницы. Это обещания школы ученикам, поэтому собраны в одном месте:
 * формулировки правятся без касания вёрстки. Цифры, имена инструкторов и цены
 * сюда не попадают — они приходят из API и не могут разойтись с реальностью.
 * -------------------------------------------------------------------------- */

/*
 * Публичной записью управляет администратор: «Настройки школы» → «Запись через
 * сайт». Флаг приходит в `PublicSchoolDto.isBookingOpen` и решает не только
 * судьбу кнопки «Выбрать время»: тексты, которые обещают запись за минуту,
 * переключаются вместе с ним, иначе страница обещала бы то, чего на ней нет.
 * Поэтому наборы блоков — функции от флага, а не константы.
 */

/*
 * Площадка на карте. В 2ГИС это точка въезда, отдельного адреса у неё нет,
 * поэтому на странице только город и карта — выдумывать улицу нельзя, по ней
 * человек приедет не туда.
 */
const LOCATION = {
  city: 'Алматы',
  lat: 43.242542,
  lon: 76.932428,
  url: 'https://2gis.kz/almaty/geo/9430360938458278',
};

const HERO = {
  badge: 'Категории A и A1',
  title: 'Мотошкола в Алматы',
  subtitle: {
    open:
      'Обучение вождению мотоцикла, категории A и A1. Занятия один на один ' +
      'с инструктором на площадке: выбираете время в расписании и записываетесь ' +
      'за минуту, без звонков и регистрации.',
    closed:
      'Обучение вождению мотоцикла, категории A и A1. Занятия один на один ' +
      'с инструктором на площадке — позвоните или напишите, подберём время ' +
      'под ваш график.',
  },
};

/** Кому подойдут занятия. Это описание аудитории, а не обещание результата. */
const AUDIENCES: Array<{ icon: MotoIconName; title: string; text: string }> = [
  {
    icon: 'helmet',
    title: 'С нуля',
    text: 'Никогда не садились за руль мотоцикла — начнём со знакомства с техникой.',
  },
  {
    icon: 'road',
    title: 'Есть опыт',
    text: 'Ездили раньше и хотите закрыть пробелы в технике управления.',
  },
  {
    icon: 'clipboard',
    title: 'Готовитесь к экзамену',
    text: 'Отрабатываем упражнения площадки до уверенного выполнения.',
  },
  {
    icon: 'rotate',
    title: 'Возвращаетесь в седло',
    text: 'Был перерыв в несколько сезонов и нужно вернуть навык.',
  },
];

/*
 * Упражнения, из которых инструктор собирает занятие, лежат рядом со своими
 * схемами в `ExerciseFigure` — название и чертёж описывают одно и то же и
 * разъезжаться не должны. Конкретный набор зависит от уровня ученика, поэтому
 * список подаётся как «что отрабатываем», а не как фиксированная программа.
 */

function benefits(
  isBookingOpen: boolean,
): Array<{ icon: MotoIconName; title: string; text: string }> {
  return [
    {
      icon: 'moto',
      title: 'Один на один',
      text: 'Всё занятие инструктор работает только с вами, а не с группой.',
    },
    isBookingOpen
      ? {
          icon: 'calendar',
          title: 'Удобное время',
          text: 'Расписание открыто целиком: видно всё свободное время на недели вперёд.',
        }
      : {
          icon: 'calendar',
          title: 'Время под вас',
          text: 'Подбираем время занятия под ваш график, а не под общий поток.',
        },
    isBookingOpen
      ? {
          icon: 'bolt',
          title: 'Запись за минуту',
          text: 'Выбрали время и длительность, оставили имя и телефон — всё. Аккаунт создадим сами.',
        }
      : {
          icon: 'bolt',
          title: 'Начать просто',
          text: 'Звонок или сообщение — инструктор ответит и назначит первое занятие.',
        },
    {
      icon: 'wallet',
      title: 'Прозрачный счёт',
      text: 'В личном кабинете видно оплаченные часы, историю занятий и остаток.',
    },
  ];
}

function steps(isBookingOpen: boolean) {
  return isBookingOpen
    ? [
        {
          title: 'Выберите время',
          text: 'Откройте расписание, выберите день, начало и сколько часов ездить.',
        },
        {
          title: 'Оставьте контакты',
          text: 'Имя и телефон — этого хватит, чтобы инструктор с вами связался.',
        },
        {
          title: 'Приезжайте на занятие',
          text: 'Инструктор подтвердит запись и откроет вам личный кабинет.',
        },
      ]
    : [
        {
          title: 'Напишите или позвоните',
          text: 'Телефон, WhatsApp или Instagram — отвечает сам инструктор.',
        },
        {
          title: 'Договоритесь о времени',
          text: 'Инструктор подберёт день и час под ваш график.',
        },
        {
          title: 'Приезжайте на занятие',
          text: 'После первого занятия инструктор откроет вам личный кабинет.',
        },
      ];
}

function faq(isBookingOpen: boolean) {
  return [
    {
      question: 'Нужен ли опыт вождения?',
      answer:
        'Нет. Инструктор выстраивает занятие под ваш уровень — от первого знакомства ' +
        'с мотоциклом до отработки отдельных упражнений.',
    },
    {
      question: 'Сколько занятий нужно, чтобы уверенно ездить?',
      answer:
        'Зависит от начального уровня и того, как часто получается заниматься. ' +
        'После первого занятия инструктор скажет, на что уйдёт время именно у вас.',
    },
    {
      question: 'На каком мотоцикле проходит занятие и нужна ли экипировка?',
      answer:
        'Уточните у инструктора при подтверждении записи — он расскажет, на чём ' +
        'проходит занятие и что взять с собой.',
    },
    isBookingOpen
      ? {
          question: 'Нужно ли регистрироваться, чтобы записаться?',
          answer:
            'Нет. Достаточно выбрать время в расписании и оставить имя и телефон. ' +
            'Личный кабинет появится после того, как инструктор подтвердит запись.',
        }
      : {
          question: 'Как записаться на занятие?',
          answer:
            'Позвоните или напишите — инструктор подберёт время. Регистрация не нужна: ' +
            'личный кабинет с историей занятий и оплат он откроет вам сам.',
        },
    {
      question: 'Как оплачивать занятия?',
      answer:
        'Оплата пакетом часов у инструктора. Все поступления и списания видны ' +
        'в личном кабинете, там же — сколько часов осталось.',
    },
    {
      question: 'Можно ли перенести занятие?',
      answer:
        'Да, свяжитесь с инструктором заранее — он отменит запись, и время ' +
        'вернётся в расписание.',
    },
  ];
}

function toIntervals(free: PublicRangeDto[]) {
  return free.map((range) => ({
    startsAt: new Date(range.startsAt),
    endsAt: new Date(range.endsAt),
  }));
}

/** Варианты начала занятия внутри свободных кусков — те же, что на /schedule. */
function startsWithin(free: PublicRangeDto[]): Date[] {
  return bookingStartOptions({
    free: toIntervals(free),
    minMinutes: MIN_LESSON_MINUTES,
    stepMinutes: BOOKING_START_STEP_MINUTES,
  });
}

/* -------------------------------------------------------------------------- */
/* Строительные блоки страницы                                                */
/* -------------------------------------------------------------------------- */

/**
 * Кнопки лендинга. Общий `Button` рассчитан на светлый интерфейс кабинета:
 * перекрывать его фон и цвет текста классами — значит зависеть от порядка
 * правил в собранном CSS. Здесь кнопки всё равно ведут на другие страницы,
 * поэтому это ссылки со своим оформлением.
 */
const CTA_BASE =
  'inline-flex min-h-13 items-center justify-center gap-2 rounded-xl px-6 text-sm font-semibold uppercase tracking-wide transition ' +
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent';

function CtaPrimary({
  to,
  children,
  className,
}: {
  to: string;
  children: ReactNode;
  className?: string;
}): React.JSX.Element {
  return (
    <Link
      to={to}
      className={cn(
        CTA_BASE,
        'bg-accent text-night shadow-lg shadow-accent/20 hover:bg-accent-600 active:scale-[0.99]',
        className,
      )}
    >
      {children}
      <MotoIcon name="arrow" className="size-4" />
    </Link>
  );
}

function CtaGhost({
  to,
  children,
  className,
}: {
  to: string;
  children: ReactNode;
  className?: string;
}): React.JSX.Element {
  return (
    <Link
      to={to}
      className={cn(
        CTA_BASE,
        'border border-white/20 bg-white/5 text-white hover:border-white/40 hover:bg-white/10',
        className,
      )}
    >
      {children}
    </Link>
  );
}

/** То же, что CtaPrimary, но открывает окно, а не ведёт по адресу. */
function CtaPrimaryButton({
  onClick,
  children,
  className,
}: {
  onClick: () => void;
  children: ReactNode;
  className?: string;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        CTA_BASE,
        'bg-accent text-night shadow-lg shadow-accent/20 hover:bg-accent-600 active:scale-[0.99]',
        className,
      )}
    >
      {children}
      <MotoIcon name="arrow" className="size-4" />
    </button>
  );
}

/** То же, что CtaGhost, но открывает окно, а не ведёт по адресу. */
function CtaGhostButton({
  onClick,
  children,
  className,
}: {
  onClick: () => void;
  children: ReactNode;
  className?: string;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        CTA_BASE,
        'border border-white/20 bg-white/5 text-white hover:border-white/40 hover:bg-white/10',
        className,
      )}
    >
      {children}
    </button>
  );
}

/**
 * Встроенная карта с меткой площадки.
 *
 * Виджет 2ГИС сюда не встаёт: он умеет показывать только организации, а точка
 * школы заведена как объект на карте — на любой запрос он отвечает «Something
 * went wrong». Поэтому карта яндексовая, а кнопка под ней ведёт в 2ГИС, где
 * эта точка и живёт. Если школу заведут в 2ГИС как организацию, виджет можно
 * вернуть: `widgets.2gis.com/widget?type=firmsonmap` с её id в поле `org`.
 */
const MAP_EMBED_URL =
  `https://yandex.ru/map-widget/v1/?ll=${LOCATION.lon}%2C${LOCATION.lat}&z=16` +
  `&pt=${LOCATION.lon},${LOCATION.lat},pm2rdm`;

/**
 * Блок выезжает снизу, когда доезжает до экрана. Наблюдатель отключается
 * после первого срабатывания: обратно при прокрутке вверх ничего не прячется.
 */
function Reveal({
  children,
  className,
  delay = 0,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
}): React.JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const [isShown, setIsShown] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || isShown) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setIsShown(true);
          observer.disconnect();
        }
      },
      // Блок «включается» чуть раньше нижней кромки экрана, иначе анимация
      // начинается уже после того, как его прочитали.
      { rootMargin: '0px 0px -12% 0px' },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [isShown]);

  return (
    <div
      ref={ref}
      className={cn('reveal', isShown && 'reveal-in', className)}
      style={delay > 0 ? { transitionDelay: `${delay}ms` } : undefined}
    >
      {children}
    </div>
  );
}

/** Заголовок раздела: жёлтая метка сверху, крупное название под ней. */
function SectionHead({
  label,
  title,
  subtitle,
}: {
  label: string;
  title: string;
  subtitle?: string;
}): React.JSX.Element {
  return (
    <div className="mb-8">
      <span className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.2em] text-accent">
        <span aria-hidden className="h-px w-8 bg-accent" />
        {label}
      </span>
      <h2 className="mt-3 text-3xl font-semibold uppercase leading-[1.05] tracking-tight text-white sm:text-4xl">
        {title}
      </h2>
      {subtitle && <p className="mt-3 max-w-xl text-sm text-white/55 sm:text-base">{subtitle}</p>}
    </div>
  );
}

/** Карточка на тёмном: почти прозрачная плашка, подсветка ободка при наведении. */
const PANEL = 'rounded-card border border-white/10 bg-white/[0.04] p-5 transition';

/** Кнопка в карточке инструктора: ведёт в запись либо открывает окно связи. */
const TEACHER_ACTION =
  'inline-flex min-h-11 shrink-0 items-center rounded-xl border border-white/20 px-4 text-sm font-medium transition ' +
  'hover:border-accent hover:text-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent';

/**
 * Список упражнений вместе со схемой выбранного. Названия вроде «габаритный
 * коридор» ничего не говорят тому, кто не был на площадке, — чертёж с
 * конусами и траекторией объясняет упражнение без единого слова.
 *
 * Схема прилипает к верху экрана: на телефоне она остаётся перед глазами,
 * пока листаешь список под ней.
 */
function ExerciseBoard(): React.JSX.Element {
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    /*
      На широком экране это две колонки, на узком — схема сверху, список под
      ней. Раскладка переключается с grid на flex не ради вида: липкий элемент
      внутри grid ограничен своей ячейкой, и в одну колонку ячейка равна высоте
      самой схемы — прилипать становится не к чему. У flex-элемента ограничение
      считается по всему контейнеру, и схема держится, пока листают список.
    */
    <div className="flex flex-col gap-6 lg:grid lg:grid-cols-[minmax(0,1fr)_22rem] lg:gap-10">
      <div className="lg:order-2">
        <div className="sticky top-4 overflow-hidden rounded-card border border-white/10 bg-night-soft">
          {/* Площадка под схемой: то же зерно асфальта, что и на первом экране */}
          <div className="grain relative px-3 pt-3 pb-1">
            <ExerciseFigure key={activeIndex} index={activeIndex} />
          </div>

          <p className="border-t border-white/10 px-4 py-3 text-xs font-semibold uppercase tracking-[0.16em] text-white/70">
            {EXERCISE_TITLES[activeIndex]}
          </p>
        </div>
      </div>

      <ol className="lg:order-1">
        {EXERCISE_TITLES.map((title, index) => {
          const isActive = index === activeIndex;

          return (
            <li key={title}>
              <button
                type="button"
                onClick={() => setActiveIndex(index)}
                aria-pressed={isActive}
                className={cn(
                  'flex min-h-11 w-full items-baseline gap-4 border-b border-white/10 py-3.5 text-left text-sm transition sm:text-base',
                  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
                  isActive ? 'text-accent' : 'text-white/80 hover:text-white',
                )}
              >
                <span
                  className={cn(
                    'text-xs font-semibold tabular-nums transition',
                    isActive ? 'text-accent' : 'text-white/35',
                  )}
                >
                  {String(index + 1).padStart(2, '0')}
                </span>
                <span className="flex-1">{title}</span>
                {/* Метка ведёт взгляд от строки к схеме, на узком экране — вверх */}
                <span
                  aria-hidden
                  className={cn(
                    'h-px w-6 shrink-0 self-center bg-accent transition-opacity',
                    isActive ? 'opacity-100' : 'opacity-0',
                  )}
                />
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * Главная страница сайта: рассказывает о школе и ведёт в расписание.
 * Само расписание живёт на /schedule.
 */
export function LandingPage(): React.JSX.Element {
  const { data: school } = useQuery({
    queryKey: ['public', 'school'],
    queryFn: () => api.get<PublicSchoolDto>('/public/school', undefined, { anonymous: true }),
  });

  const { user } = useAuth();
  const [isContacting, setIsContacting] = useState(false);

  /*
   * Запись открывает администратор в настройках. Пока ответ не пришёл, считаем
   * её закрытой: показать кнопку «Выбрать время» и тут же её убрать — хуже,
   * чем показать контакты и добавить кнопку, когда настройка доехала.
   */
  const isBookingOpen = school?.isBookingOpen ?? false;

  /*
   * Заголовок и описание для выдачи. Город и категории здесь обязательны:
   * ищут «мотошкола Алматы» и «обучение на категорию A», а не название школы,
   * которое будущий ученик ещё не знает.
   */
  usePageMeta({ ...LANDING_META, path: '/' });

  useSchoolSchema({
    name: school?.name ?? 'FREEDOM BIKER',
    phone: school?.contactPhone ?? null,
    instagram: school?.contactInstagram ?? null,
    isBookingOpen,
    faq: faq(isBookingOpen),
    geo: { lat: LOCATION.lat, lon: LOCATION.lon, city: LOCATION.city },
  });

  /*
   * Куда ведут кнопки записи. Гостю — публичная страница выбора времени,
   * ученику — та же страница внутри кабинета, где заявка уходит от его имени.
   * Сотруднику записываться не на что, поэтому он попадает в своё расписание.
   */
  const bookHref = user?.role === 'STUDENT' ? '/book' : '/schedule';

  const { data: schedule } = useQuery({
    queryKey: ['public', 'schedule'],
    queryFn: () =>
      api.get<PublicOpenScheduleDto>('/public/schedule', undefined, { anonymous: true }),
  });

  const teachers = schedule?.teachers ?? [];
  const currency = schedule?.currency ?? 'KZT';
  const timeZone = schedule?.timeZone ?? 'Asia/Almaty';

  /*
   * Готовых окон в расписании нет: инструктор задаёт часы работы, а время
   * выбирает ученик. Считаем то же, что он увидит на /schedule, — с какого
   * часа можно начать занятие.
   */
  const freeStarts = teachers.flatMap((teacher) => startsWithin(teacher.free));
  /*
   * Одно и то же время у двух инструкторов на лендинге не нужно: он показывает,
   * когда вообще можно приехать, а к кому — выбирают уже в расписании.
   */
  const upcoming = [...new Set(freeStarts.map((date) => date.toISOString()))]
    .sort()
    .slice(0, 6);
  const freeHours = Math.round(
    teachers.reduce((sum, teacher) => sum + totalMinutes(toIntervals(teacher.free)), 0) / 60,
  );

  /*
   * Свободные часы показываем только вместе с записью: без неё цифра ведёт
   * в расписание, которого на странице нет.
   */
  const stats = [
    ...(isBookingOpen ? [{ label: 'Свободных часов', value: String(freeHours) }] : []),
    { label: 'Инструкторов', value: String(teachers.length) },
  ];

  const rates = teachers
    .map((teacher) => teacher.hourlyRateMinor)
    .filter((rate): rate is number => rate !== null);
  const minRate = rates.length > 0 ? Math.min(...rates) : null;
  stats.push({
    label: 'Занятие от',
    value: minRate === null ? '—' : formatMoney(minRate, currency),
  });

  return (
    <div className="min-h-dvh bg-night text-white">
      <header className="safe-top absolute inset-x-0 z-20 px-4 pb-3">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3">
          <span className="flex min-w-0 items-center gap-2">
            <Logo className="size-9" />
            <SchoolName name={school?.name ?? 'Мотошкола'} className="truncate text-sm" />
          </span>
          <Link
            to={user ? '/schedule' : '/login'}
            className="min-h-11 shrink-0 rounded-xl px-3 text-sm font-medium text-white/70 transition hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent inline-flex items-center"
          >
            {user ? 'В кабинет' : 'Войти'}
          </Link>
        </div>
      </header>

      {/* ---------------------------------------------------------- первый экран */}
      <section className="grain relative overflow-hidden px-4 pt-28 pb-20 sm:pt-32 sm:pb-28">
        {/*
          Фон-«асфальт»: тёмная база, свет фар сверху и зернистость из `.grain`.
          Фотографий у школы пока нет, а сплошная заливка выглядит пусто.
          Оба пятна тёплые: на баннере ровно два цвета, и синему тут взяться
          неоткуда.
        */}
        <div
          aria-hidden
          className="absolute inset-0 bg-night"
          style={{
            backgroundImage:
              'radial-gradient(ellipse 80% 55% at 50% -12%, rgba(244,196,48,0.18), transparent 68%),' +
              'radial-gradient(ellipse 55% 40% at 88% 96%, rgba(244,196,48,0.07), transparent 72%)',
          }}
        />

        {/* Дорожная разметка уходит вдаль по нижнему краю экрана */}
        <div
          aria-hidden
          className="road-marks absolute inset-x-0 bottom-0 h-4 opacity-45"
          style={{
            maskImage: 'linear-gradient(to top, black, transparent)',
            WebkitMaskImage: 'linear-gradient(to top, black, transparent)',
          }}
        />

        <div className="relative mx-auto max-w-3xl">
          <span className="rise inline-flex items-center gap-2 rounded-full border border-accent/30 bg-accent/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-accent">
            <span aria-hidden className="size-1.5 rounded-full bg-accent" />
            {HERO.badge}
          </span>

          <h1
            // Unbounded заметно шире системного гротеска: тот же кегль занимает
            // в полтора раза больше места и ломает строку в неожиданном месте
            className="rise mt-6 text-4xl font-bold uppercase leading-[1.02] tracking-tight text-balance sm:text-6xl"
            style={{ animationDelay: '80ms' }}
          >
            {/*
              Пробел перед переносом обязателен: без него текстовое содержимое
              заголовка склеивается в «Уверенная ездана мотоцикле» — именно так
              его прочитает скринридер и проиндексирует поисковик.
            */}
            {/*
              В заголовке то, что человек набирает в поиске: «мотошкола
              Алматы». Обещание про уверенную езду переехало в подзаголовок —
              оно про результат, но по нему школу не ищут.
            */}
            Мотошкола{' '}
            <br />
            <span className="text-accent">в Алматы</span>
          </h1>

          <p
            className="rise mt-6 max-w-xl text-base leading-relaxed text-white/60 sm:text-lg"
            style={{ animationDelay: '160ms' }}
          >
            {isBookingOpen ? HERO.subtitle.open : HERO.subtitle.closed}
          </p>

          <div
            className="rise mt-9 flex flex-col gap-3 sm:flex-row"
            style={{ animationDelay: '240ms' }}
          >
            {isBookingOpen ? (
              <>
                <CtaPrimary to={bookHref} className="sm:w-auto">
                  Выбрать время
                </CtaPrimary>
                {/* Записаться можно не всем и не всегда — вопрос задают чаще */}
                <CtaGhostButton onClick={() => setIsContacting(true)}>Связаться</CtaGhostButton>
              </>
            ) : (
              <CtaPrimaryButton onClick={() => setIsContacting(true)} className="sm:w-auto">
                Связаться
              </CtaPrimaryButton>
            )}
          </div>

          {/* Цифры настоящие: приходят из расписания, а не написаны от руки */}
          <dl
            className={cn(
              'rise mt-12 grid max-w-xl divide-x divide-white/10 border-t border-white/10 pt-6',
              stats.length === 3 ? 'grid-cols-3' : 'grid-cols-2',
            )}
            style={{ animationDelay: '320ms' }}
          >
            {stats.map((stat, index) => (
              <div key={stat.label} className={cn('min-w-0', index > 0 && 'pl-4')}>
                {/*
                  Моноширинные цифры шире пропорциональных, и «10 000 ₸» в трети
                  экрана телефона не помещается — знак валюты срезало. На узком
                  экране кегль на ступень меньше, с sm возвращается прежний.
                */}
                <dd className="text-xl font-semibold tabular-nums sm:text-3xl">{stat.value}</dd>
                <dt className="mt-1 text-[11px] uppercase tracking-wide text-white/40">
                  {stat.label}
                </dt>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <main className="mx-auto max-w-5xl space-y-20 px-4 py-20 sm:space-y-24">
        {/* ------------------------------------------------------- кому подойдёт */}
        <Reveal>
          <section>
            <SectionHead
              label="Кому подойдёт"
              title="Занятие под вашу задачу"
              subtitle="Программа собирается под ваш уровень, а не под общий поток"
            />

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {AUDIENCES.map((item) => (
                <div key={item.title} className={cn(PANEL, 'h-full hover:border-accent/40')}>
                  <MotoIcon name={item.icon} className="size-7 text-accent" />
                  <p className="mt-4 font-semibold uppercase tracking-wide">{item.title}</p>
                  <p className="mt-2 text-sm leading-relaxed text-white/55">{item.text}</p>
                </div>
              ))}
            </div>
          </section>
        </Reveal>

        {/* --------------------------------------------------- что отрабатываем */}
        <Reveal>
          <section>
            <SectionHead
              label="Что отрабатываем"
              title="Упражнения площадки"
              subtitle="Инструктор подбирает упражнения под ваш уровень"
            />

            <ExerciseBoard />
          </section>
        </Reveal>

        {/* -------------------------------------------------- ближайшее время */}
        {isBookingOpen && upcoming.length > 0 && (
          <Reveal>
            <section>
              <SectionHead
                label="Ближайшее время"
                title="Когда можно начать"
                subtitle="Нажмите на время, чтобы записаться"
              />

              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
                {upcoming.map((startsAt) => (
                  <Link
                    key={startsAt}
                    to={bookHref}
                    className="group rounded-card border border-white/10 bg-white/[0.04] p-4 text-center transition hover:-translate-y-0.5 hover:border-accent hover:bg-accent/10"
                  >
                    <p className="text-[11px] uppercase tracking-wide text-white/40">
                      {humanDayLabel(startsAt, timeZone)}
                    </p>
                    <p className="mt-1 text-xl font-semibold tabular-nums transition group-hover:text-accent">
                      {formatTime(startsAt, timeZone)}
                    </p>
                  </Link>
                ))}
              </div>

              <div className="mt-6">
                <CtaGhost to={bookHref}>Смотреть всё расписание</CtaGhost>
              </div>
            </section>
          </Reveal>
        )}

        {/* ----------------------------------------------------- как записаться */}
        <Reveal>
          <section>
            <SectionHead label="Как записаться" title="Три шага до площадки" />

            <ol className="grid gap-3 sm:grid-cols-3">
              {steps(isBookingOpen).map((step, index) => (
                <li key={step.title} className={cn(PANEL, 'relative h-full overflow-hidden')}>
                  {/* Крупный номер уходит в фон — он ведёт взгляд, но не спорит с текстом */}
                  <span
                    aria-hidden
                    className="absolute -top-3 right-2 text-7xl font-semibold leading-none text-white/[0.06]"
                  >
                    {index + 1}
                  </span>
                  <div className="relative">
                    <span className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">
                      Шаг {index + 1}
                    </span>
                    <p className="mt-3 font-semibold uppercase tracking-wide">{step.title}</p>
                    <p className="mt-2 text-sm leading-relaxed text-white/55">{step.text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>
        </Reveal>

        {/* --------------------------------------------------------- преимущества */}
        <Reveal>
          <section>
            <SectionHead label="Как это устроено" title="Почему так удобно" />

            <div className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
              {benefits(isBookingOpen).map((benefit) => (
                <div key={benefit.title} className="flex gap-4">
                  <span className="flex size-11 shrink-0 items-center justify-center rounded-xl border border-accent/25 bg-accent/10 text-accent">
                    <MotoIcon name={benefit.icon} className="size-5" />
                  </span>
                  <div className="min-w-0">
                    <p className="font-semibold uppercase tracking-wide">{benefit.title}</p>
                    <p className="mt-1.5 text-sm leading-relaxed text-white/55">{benefit.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </Reveal>

        {/* ------------------------------------------------------------ инструкторы */}
        {teachers.length > 0 && (
          <Reveal>
            <section>
              <SectionHead label="Кто будет учить" title="Инструкторы" />

              <div className="grid gap-3 sm:grid-cols-2">
                {teachers.map((teacher) => {
                  const free = Math.round(totalMinutes(toIntervals(teacher.free)) / 60);

                  return (
                    <div
                      key={teacher.id}
                      className={cn(PANEL, 'flex h-full flex-col hover:border-accent/40')}
                    >
                      <div className="flex items-center gap-3">
                        <Avatar name={teacher.name} tone="dark" />
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-semibold">{teacher.name}</p>
                          <p className="text-sm text-accent">
                            {teacher.hourlyRateMinor === null
                              ? 'Индивидуальные занятия'
                              : `${formatMoney(teacher.hourlyRateMinor, currency)} за час`}
                          </p>
                        </div>
                        {isBookingOpen ? (
                          <Link
                            to={bookHref}
                            className={TEACHER_ACTION}
                          >
                            Записаться
                          </Link>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setIsContacting(true)}
                            className={TEACHER_ACTION}
                          >
                            Связаться
                          </button>
                        )}
                      </div>

                      {/* Свободные часы обещают запись — без неё строка лишняя */}
                      {isBookingOpen && (
                        <p className="mt-4 text-xs uppercase tracking-wide text-white/40">
                          {free > 0
                            ? `${free} свободных часов в ближайшие недели`
                            : 'Свободного времени сейчас нет'}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          </Reveal>
        )}

        {/* ------------------------------------------------------------------ карта */}
        <Reveal>
          <section>
            <SectionHead label="Где занимаемся" title={LOCATION.city} />

            {/*
              Виджет 2ГИС светлый и с собственными скруглениями по углам —
              рамка с overflow-hidden сажает его в ту же карточку, что и всё
              остальное на странице.
            */}
            <div className="overflow-hidden rounded-card border border-white/10 bg-night-soft">
              <iframe
                src={MAP_EMBED_URL}
                title={`Площадка школы на карте, ${LOCATION.city}`}
                loading="lazy"
                className="block h-80 w-full border-0 sm:h-[420px]"
              />
            </div>

            <a
              href={LOCATION.url}
              target="_blank"
              rel="noreferrer"
              className={cn(CTA_BASE, 'mt-4 border border-white/20 bg-white/5 text-white hover:border-white/40 hover:bg-white/10')}
            >
              Открыть в 2ГИС
              <MotoIcon name="arrow" className="size-4" />
            </a>
          </section>
        </Reveal>

        {/* ------------------------------------------------------------- занятия */}
        <Reveal>
          <section>
            <SectionHead
              label="Чему учим"
              title="Занятия и категории"
              subtitle="Отдельная страница под каждую задачу — с чего начать, зависит от вашего опыта"
            />

            <div className="grid gap-3 sm:grid-cols-3">
              {COURSES.map((course) => (
                <Link
                  key={course.slug}
                  to={`/${course.slug}`}
                  className={cn(PANEL, 'block h-full hover:border-accent/40')}
                >
                  <p className="font-semibold uppercase tracking-wide">
                    {course.h1.plain} <span className="text-accent">{course.h1.accent}</span>
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-white/55">{course.short}</p>
                  <span className="mt-4 inline-flex items-center gap-2 text-sm text-accent">
                    Подробнее
                    <MotoIcon name="arrow" className="size-4" />
                  </span>
                </Link>
              ))}
            </div>
          </section>
        </Reveal>

        {/* -------------------------------------------------------------------- FAQ */}
        <Reveal>
          <section>
            <SectionHead label="Вопросы" title="Частые вопросы" />

            <div className="border-t border-white/10">
              {faq(isBookingOpen).map((item) => (
                <details key={item.question} className="group border-b border-white/10">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-4 font-medium transition hover:text-accent marker:content-['']">
                    {item.question}
                    <span
                      aria-hidden
                      className="relative size-5 shrink-0 text-accent transition-transform duration-300 group-open:rotate-45"
                    >
                      <span className="absolute left-1/2 top-1/2 h-px w-4 -translate-x-1/2 -translate-y-1/2 bg-current" />
                      <span className="absolute left-1/2 top-1/2 h-4 w-px -translate-x-1/2 -translate-y-1/2 bg-current" />
                    </span>
                  </summary>
                  <p className="pb-5 pr-9 text-sm leading-relaxed text-white/55">{item.answer}</p>
                </details>
              ))}
            </div>
          </section>
        </Reveal>

        {/* ----------------------------------------------------------------- призыв */}
        <Reveal>
          <section>
            {/*
              Единственный жёлтый блок на странице. Он же последний экран перед
              подвалом — контраст работает как точка остановки.
            */}
            <div className="relative overflow-hidden rounded-card bg-accent px-6 py-12 text-night sm:px-12 sm:py-14">
              <div
                aria-hidden
                className="absolute inset-x-0 bottom-0 h-1.5"
                style={{
                  backgroundImage:
                    'repeating-linear-gradient(90deg, rgba(10,14,23,0.85) 0 24px, transparent 24px 44px)',
                }}
              />

              <h2 className="max-w-lg text-3xl font-semibold uppercase leading-[1.05] tracking-tight sm:text-4xl">
                Начните с первого занятия
              </h2>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-night/70 sm:text-base">
                {isBookingOpen
                  ? 'Выберите свободное время — инструктор перезвонит, подтвердит запись и заведёт вам личный кабинет.'
                  : 'Позвоните или напишите — инструктор подберёт время, проведёт занятие и заведёт вам личный кабинет.'}
              </p>

              {isBookingOpen ? (
                <Link
                  to={bookHref}
                  className={cn(
                    CTA_BASE,
                    'mt-8 bg-night text-white hover:bg-night-soft active:scale-[0.99]',
                  )}
                >
                  Выбрать время
                  <MotoIcon name="arrow" className="size-4" />
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={() => setIsContacting(true)}
                  className={cn(
                    CTA_BASE,
                    'mt-8 bg-night text-white hover:bg-night-soft active:scale-[0.99]',
                  )}
                >
                  Связаться
                  <MotoIcon name="arrow" className="size-4" />
                </button>
              )}

              {/* Контакты те же, что в окне связи: источник один — настройки школы */}
              {(school?.contactPhone || school?.contactInstagram) && (
                <p className="mt-6 text-sm font-medium text-night/70">
                  Есть вопросы?{' '}
                  {school.contactPhone && (
                    <a href={`tel:+${phoneDigits(school.contactPhone)}`} className="underline">
                      {school.contactPhone}
                    </a>
                  )}
                  {school.contactPhone && school.contactInstagram && <span>{' · '}</span>}
                  {school.contactInstagram && (
                    <a
                      href={`https://www.instagram.com/${school.contactInstagram}`}
                      target="_blank"
                      rel="noreferrer"
                      className="underline"
                    >
                      Instagram
                    </a>
                  )}
                </p>
              )}
            </div>
          </section>
        </Reveal>
      </main>

      {/* Снизу оставлено место под фиксированную кнопку — иначе она накрывает подвал */}
      <footer className="border-t border-white/10 px-4 pt-8 pb-28 lg:pb-8">
        <div className="mx-auto flex max-w-5xl flex-col items-center gap-3 text-center sm:flex-row sm:justify-between sm:text-left">
          <span className="flex items-center gap-2">
            <Logo className="size-8" />
            <SchoolName name={school?.name ?? 'Мотошкола'} className="text-sm" />
          </span>
          <p className="text-sm text-white/50">
            {isBookingOpen && (
              <>
                <Link to="/schedule" className="transition hover:text-accent">
                  Расписание
                </Link>
                {' · '}
              </>
            )}
            <Link to={user ? '/schedule' : '/login'} className="transition hover:text-accent">
              {user ? 'Личный кабинет' : 'Вход для учеников'}
            </Link>
          </p>
        </div>
      </footer>

      {/* Кнопка под большим пальцем: на телефоне до неё дотянуться проще всего */}
      {/*
        Окно связи светлое, как остальные диалоги приложения: это не часть
        витрины, а список ссылок, по которым сейчас будут тыкать пальцем.
      */}
      <ContactSheet
        school={school}
        isOpen={isContacting}
        onClose={() => setIsContacting(false)}
      />

      <div className="safe-bottom fixed inset-x-0 bottom-0 z-20 flex gap-2 border-t border-white/10 bg-night/90 px-4 pt-3 backdrop-blur lg:hidden">
        {isBookingOpen ? (
          <>
            <CtaPrimary to={bookHref} className="flex-1">
              Выбрать время
            </CtaPrimary>
            <CtaGhostButton onClick={() => setIsContacting(true)} className="shrink-0">
              Связаться
            </CtaGhostButton>
          </>
        ) : (
          <CtaPrimaryButton onClick={() => setIsContacting(true)} className="flex-1">
            Связаться
          </CtaPrimaryButton>
        )}
      </div>
    </div>
  );
}
